import { useEffect, useMemo, useState } from 'react';
import { useCustom, useApiUrl } from '@refinedev/core';
import { Loader2, TrendingUp, Receipt, Package, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ColorPicker } from '@/components/ui/color-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { OrderStatusConfig } from './types';

const TARGET_TYPE_LABELS: Record<string, string> = {
  COURSE: 'Курсы',
  SUBSCRIPTION_PLAN: 'Абонементы',
  CHANNEL_PASS: 'Каналы',
  BONUS: 'Привилегии',
  EVENT: 'События',
};

const currencyFormatter = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function formatCurrency(value: string | number, currency: string): string {
  const num = typeof value === 'string' ? Number.parseFloat(value) : value;
  return `${currencyFormatter.format(num)} ${currency}`;
}

interface AnalyticsResponse {
  ordersCount: number;
  paymentsCount: number;
  byCurrency: Record<
    string,
    { ordersCount: number; totalSum: string; paidSum: string; avgOrder: string }
  >;
  byTargetType: Record<string, number>;
  byDay: Array<{ date: string; ordersCount: number; totalSum: Record<string, string> }>;
}

interface StatusFormData {
  name: string;
  color: string;
  description: string;
  isInitial: boolean;
  isFinal: boolean;
}

interface StatusAnalyticsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: OrderStatusConfig | null;
  kanbanQuery: Record<string, string>;
  onSaveStatus: (data: StatusFormData) => void;
  onDeleteStatus?: () => void;
  onAdvanced?: () => void;
  isSavingStatus: boolean;
}

const DEFAULT_STATUS_FORM: StatusFormData = {
  name: '',
  color: '#6b7280',
  description: '',
  isInitial: false,
  isFinal: false,
};

export function StatusAnalyticsModal({
  open,
  onOpenChange,
  status,
  kanbanQuery,
  onSaveStatus,
  onDeleteStatus,
  onAdvanced,
  isSavingStatus,
}: StatusAnalyticsModalProps) {
  const apiUrl = useApiUrl();
  const isEdit = !!status;
  const [tab, setTab] = useState<'analytics' | 'edit'>(isEdit ? 'analytics' : 'edit');

  useEffect(() => {
    if (open) {
      setTab(isEdit ? 'analytics' : 'edit');
    }
  }, [open, isEdit]);

  // Status edit form state
  const [form, setForm] = useState<StatusFormData>(DEFAULT_STATUS_FORM);
  useEffect(() => {
    if (open && status) {
      setForm({
        name: status.name,
        color: status.color,
        description: status.description ?? '',
        isInitial: status.isInitial,
        isFinal: status.isFinal,
      });
    } else if (open) {
      setForm(DEFAULT_STATUS_FORM);
    }
  }, [open, status]);

  // Analytics query — only when modal is open and we have a status
  const analyticsEnabled = open && isEdit && tab === 'analytics';
  const { result: analyticsResult, query: analyticsQuery } = useCustom<AnalyticsResponse>({
    url: `${apiUrl}/orders/kanban/analytics/${status?.id ?? 0}`,
    method: 'get',
    config: { query: kanbanQuery },
    queryOptions: { enabled: analyticsEnabled },
  });
  const analytics = analyticsResult?.data;
  const isLoadingAnalytics = analyticsQuery?.isLoading ?? false;

  const isSystem = status?.isSystem ?? false;

  const handleSubmit = () => {
    if (!form.name.trim()) { return; }
    onSaveStatus(form);
  };

  // Prepare chart data for last 30 days (orders per day)
  const chartData = useMemo(() => {
    if (!analytics?.byDay) { return []; }
    return analytics.byDay.map((d) => ({
      date: d.date.slice(5), // MM-DD
      count: d.ordersCount,
    }));
  }, [analytics?.byDay]);

  const currencies = analytics?.byCurrency ? Object.keys(analytics.byCurrency) : [];
  const targetTypes = analytics?.byTargetType
    ? Object.entries(analytics.byTargetType).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {status && (
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: status.color }}
              />
            )}
            <span>{isEdit ? status?.name : 'Новый статус'}</span>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'analytics' | 'edit')} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="analytics" disabled={!isEdit}>
              <TrendingUp className="h-4 w-4 mr-1.5" />
              Аналитика
            </TabsTrigger>
            <TabsTrigger value="edit">
              {isEdit ? 'Редактирование' : 'Создание'}
            </TabsTrigger>
          </TabsList>

          {/* --- Analytics tab --- */}
          <TabsContent value="analytics" className="space-y-4 pt-2">
            {isLoadingAnalytics && (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Загрузка аналитики...
              </div>
            )}

            {!isLoadingAnalytics && analytics && (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-3 gap-2">
                  <Card>
                    <CardContent className="flex flex-col gap-1 py-3 px-3">
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Receipt className="h-3 w-3" />
                        Заказов
                      </div>
                      <div className="text-xl font-semibold font-mono">
                        {(analytics.ordersCount ?? 0).toLocaleString('ru-RU')}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="flex flex-col gap-1 py-3 px-3">
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <TrendingUp className="h-3 w-3" />
                        Платежей
                      </div>
                      <div className="text-xl font-semibold font-mono">
                        {(analytics.paymentsCount ?? 0).toLocaleString('ru-RU')}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="flex flex-col gap-1 py-3 px-3">
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Package className="h-3 w-3" />
                        Товаров
                      </div>
                      <div className="text-xl font-semibold font-mono">
                        {Object.values(analytics.byTargetType ?? {})
                          .reduce((s, n) => s + n, 0)
                          .toLocaleString('ru-RU')}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Currency breakdown */}
                {currencies.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">По валютам</h4>
                    <div className="space-y-1.5">
                      {currencies.map((cur) => {
                        const row = analytics.byCurrency[cur];
                        return (
                          <div
                            key={cur}
                            className="rounded-lg border border-border/50 p-2.5 bg-muted/20"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="font-mono text-[11px]">{cur}</Badge>
                                <span className="text-xs text-muted-foreground">
                                  {row.ordersCount} заказов · средний {formatCurrency(row.avgOrder, cur)}
                                </span>
                              </div>
                            </div>
                            <div className="mt-1.5 grid grid-cols-2 gap-1 text-xs font-mono">
                              <div>
                                <span className="text-muted-foreground">Оплачено: </span>
                                <span className="text-emerald-600 font-medium">
                                  {formatCurrency(row.paidSum, cur)}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Итого: </span>
                                <span className="font-medium">
                                  {formatCurrency(row.totalSum, cur)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Target type distribution */}
                {targetTypes.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">По типам продуктов</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {targetTypes.map(([type, cnt]) => (
                        <Badge key={type} variant="secondary" className="text-[11px]">
                          {TARGET_TYPE_LABELS[type] ?? type}: {cnt.toLocaleString('ru-RU')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Daily chart */}
                {chartData.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">
                      Заказы за последние 30 дней
                    </h4>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip
                          contentStyle={{
                            fontSize: 12,
                            borderRadius: 8,
                            border: '1px solid hsl(var(--border))',
                            backgroundColor: 'hsl(var(--background))',
                          }}
                        />
                        <Bar dataKey="count" fill={status?.color ?? '#6366f1'} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {analytics.ordersCount === 0 && (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    Нет заказов в этом статусе с текущими фильтрами
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* --- Edit tab --- */}
          <TabsContent value="edit" className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="status-name">Название</Label>
              <Input
                id="status-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="В работе"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status-color">Цвет</Label>
              <ColorPicker
                id="status-color"
                value={form.color}
                onChange={(color) => setForm({ ...form, color })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status-description">Описание</Label>
              <Input
                id="status-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Краткое описание статуса"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Начальный статус</Label>
                <p className="text-xs text-muted-foreground">Присваивается новым заказам</p>
              </div>
              <Switch
                checked={form.isInitial}
                onCheckedChange={(checked) => setForm({ ...form, isInitial: checked })}
                disabled={isSystem}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Финальный статус</Label>
                <p className="text-xs text-muted-foreground">Заказ больше не может измениться</p>
              </div>
              <Switch
                checked={form.isFinal}
                onCheckedChange={(checked) => setForm({ ...form, isFinal: checked })}
                disabled={isSystem}
              />
            </div>

            {isEdit && onAdvanced && (
              <Button
                variant="link"
                size="sm"
                className="px-0 text-muted-foreground"
                onClick={onAdvanced}
              >
                Расширенные настройки (действия, переходы)
              </Button>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex-row justify-between sm:justify-between border-t pt-3">
          <div>
            {isEdit && !isSystem && onDeleteStatus && tab === 'edit' && (
              <Button variant="destructive" size="sm" onClick={onDeleteStatus}>
                <Trash2 className="h-4 w-4 mr-1" />
                Удалить
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <DialogClose asChild>
              <Button variant="outline">Закрыть</Button>
            </DialogClose>
            {tab === 'edit' && (
              <Button onClick={handleSubmit} disabled={isSavingStatus || !form.name.trim()}>
                {isSavingStatus ? 'Сохранение...' : (isEdit ? 'Сохранить' : 'Создать')}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Unused import helper — keep ref so linter doesn't complain when field not used.
export type { StatusFormData };
